/**
 * Master Workflow + Statuses admin (W1).
 *
 * One page with two tabs: Workflow (pipeline view) and Statuses (table edit).
 * Admin-only writes; reads OK for any authenticated user.
 * No automation in W1 — disabled placeholders below.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GitBranch, Pencil, ArrowUp, ArrowDown, CheckCircle2, XCircle, Settings2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WorkflowActionsPanel from "./WorkflowActionsPanel";

const COLOR_OPTIONS = ["blue", "purple", "indigo", "amber", "orange", "teal", "slate", "navy", "green", "cyan", "rose"];

const COLOR_CLASS = {
    blue:   "bg-blue-100 text-blue-800 border-blue-300",
    purple: "bg-purple-100 text-purple-800 border-purple-300",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
    amber:  "bg-amber-100 text-amber-800 border-amber-300",
    orange: "bg-orange-100 text-orange-800 border-orange-300",
    teal:   "bg-teal-100 text-teal-800 border-teal-300",
    slate:  "bg-slate-100 text-slate-800 border-slate-300",
    navy:   "bg-blue-200 text-blue-900 border-blue-400",
    green:  "bg-emerald-100 text-emerald-800 border-emerald-300",
    cyan:   "bg-cyan-100 text-cyan-800 border-cyan-300",
    rose:   "bg-rose-100 text-rose-800 border-rose-300",
};

export function StatusBadge({ color, name, size = "sm", "data-testid": testId }) {
    const cls = COLOR_CLASS[color] || COLOR_CLASS.slate;
    return (
        <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-medium tracking-wide uppercase ${cls} ${size === "lg" ? "text-xs" : "text-[10.5px]"}`}
              data-testid={testId}>
            {name}
        </span>
    );
}

export default function WorkflowAdmin() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const initialTab = params.get("tab") === "statuses" ? "statuses" : "workflow";
    const [tab, setTab] = useState(initialTab);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const isAdmin = user?.role === "admin";

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/workflow/statuses");
            setStatuses((r.data || []).sort((a, b) => a.order - b.order));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const move = async (statusId, direction) => {
        try {
            await api.post(`/workflow/statuses/${statusId}/move?direction=${direction}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="workflow-admin-page">
            <header className="mb-6 flex items-center gap-3">
                <GitBranch className="h-7 w-7 text-[#0c2a4d]" />
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Master Workflow</h1>
                    <p className="text-sm text-muted-foreground">One pipeline for the entire CRM. All leads and clients move through this sequence.</p>
                </div>
            </header>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
                    <TabsTrigger value="statuses" data-testid="tab-statuses">Statuses</TabsTrigger>
                </TabsList>

                {/* ── Workflow (pipeline view) ──────────────────────────── */}
                <TabsContent value="workflow" className="mt-4 space-y-3">
                    {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
                    {statuses.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-3 shadow-sm"
                             data-testid={`pipeline-row-${s.slug}`}>
                            <div className="font-mono text-xs text-muted-foreground w-8 text-center">{i + 1}.</div>
                            <StatusBadge color={s.color} name={s.name} size="lg" />
                            <div className="flex-1 text-xs text-muted-foreground italic truncate">{s.description || "—"}</div>
                            <span className={`text-[10.5px] uppercase tracking-wide font-semibold ${s.active ? "text-emerald-700" : "text-rose-700"}`}>
                                {s.active ? "Active" : "Inactive"}
                            </span>
                            <span className="text-[10.5px] uppercase tracking-wide text-muted-foreground">0 automations</span>
                            {isAdmin && (
                                <>
                                    <Button size="icon" variant="ghost" onClick={() => move(s.id, "up")} disabled={i === 0} data-testid={`move-up-${s.slug}`}><ArrowUp className="h-4 w-4" /></Button>
                                    <Button size="icon" variant="ghost" onClick={() => move(s.id, "down")} disabled={i === statuses.length - 1} data-testid={`move-down-${s.slug}`}><ArrowDown className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`edit-${s.slug}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                                </>
                            )}
                        </div>
                    ))}
                    {!loading && !statuses.length && <div className="text-sm text-muted-foreground">No statuses yet.</div>}
                </TabsContent>

                {/* ── Statuses (table view) ─────────────────────────────── */}
                <TabsContent value="statuses" className="mt-4">
                    <div className="rounded-lg border border-border overflow-hidden bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr className="text-left text-[11px] uppercase tracking-wider">
                                    <th className="py-2.5 px-3 font-semibold">Order</th>
                                    <th className="py-2.5 px-3 font-semibold">Name</th>
                                    <th className="py-2.5 px-3 font-semibold">Color</th>
                                    <th className="py-2.5 px-3 font-semibold">Active</th>
                                    <th className="py-2.5 px-3 font-semibold">Description</th>
                                    <th className="py-2.5 px-3 font-semibold">Updated</th>
                                    {isAdmin && <th className="py-2.5 px-3 font-semibold text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {statuses.map((s) => (
                                    <tr key={s.id} className="border-t border-slate-100" data-testid={`status-row-${s.slug}`}>
                                        <td className="py-2.5 px-3 font-mono text-xs text-muted-foreground">{s.order}</td>
                                        <td className="py-2.5 px-3"><StatusBadge color={s.color} name={s.name} /></td>
                                        <td className="py-2.5 px-3 text-xs text-muted-foreground">{s.color}</td>
                                        <td className="py-2.5 px-3">
                                            {s.active
                                                ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                                : <XCircle className="h-4 w-4 text-rose-600" />}
                                        </td>
                                        <td className="py-2.5 px-3 text-xs text-muted-foreground italic max-w-xs truncate">{s.description || "—"}</td>
                                        <td className="py-2.5 px-3 text-xs text-muted-foreground">{(s.updated_at || s.created_at || "").slice(0, 10)}</td>
                                        {isAdmin && (
                                            <td className="py-2.5 px-3 text-right">
                                                <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`statuses-edit-${s.slug}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-600 flex items-start gap-2">
                        <Settings2 className="h-4 w-4 mt-0.5 shrink-0" />
                        <span><strong>Automations</strong> (trigger SMS / email / status moves when a status is entered or aged) will be configured in a later phase.</span>
                    </div>
                </TabsContent>
            </Tabs>

            <EditStatusDialog status={editing} allStatuses={statuses} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
        </div>
    );
}

function EditStatusDialog({ status, allStatuses, onClose, onSaved }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        setForm(status ? {
            name: status.name, description: status.description || "",
            color: status.color || "slate", active: status.active,
        } : null);
    }, [status]);
    if (!status || !form) return null;

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/workflow/statuses/${status.id}`, form);
            toast.success("Status updated.");
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!status} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto" data-testid="edit-status-dialog">
                <DialogHeader>
                    <DialogTitle>Edit Status</DialogTitle>
                    <DialogDescription>Configure the status details and the actions that will run when a client enters this status. Actions are saved as definitions only — execution ships in a later phase.</DialogDescription>
                </DialogHeader>

                {/* A. Status Details */}
                <section className="space-y-3 border-b border-slate-100 pb-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">A · Status Details</div>
                    <Field label="Name">
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
                    </Field>
                    <Field label="Description">
                        <Textarea value={form.description} rows={2} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Color">
                            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                                <SelectTrigger data-testid="edit-color"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {COLOR_OPTIONS.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Status Availability">
                            <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                                <SelectTrigger data-testid="edit-active"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="yes">Active</SelectItem>
                                    <SelectItem value="no">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <div className="flex justify-end">
                        <Button size="sm" onClick={save} disabled={busy} data-testid="edit-save-details">
                            {busy ? "Saving…" : "Save details"}
                        </Button>
                    </div>
                </section>

                {/* B + C. Actions */}
                <section className="pt-2">
                    <WorkflowActionsPanel statusId={status.id} allStatuses={allStatuses} />
                </section>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">{label}</div>
            {children}
        </label>
    );
}
